// The 30-day practice plan: skill before money. Every trade counts (real,
// practice from the ticket, and Replay), days follow the desk's 00:00 UTC
// reset, and the milestone is the bar for thinking about more real money:
// 50 trades, at least 90% of the real ones by the rules, a positive average R.
import { useSyncExternalStore } from "react";
import type { TrackedTrade } from "./deskData";
import type { ReplayLogEntry } from "../chart/replay/replay";

export const CHALLENGE_DAYS = 30;
export const GOAL_TRADES = 50;
/** Practice trades a day when no Ready signal comes (5 Replay trades ≈ 20–30 minutes). */
export const DAILY_GOAL = 5;
export const RULES_GOAL = 0.9;
const DAY_MS = 86_400_000;

export interface ChallengeTrade {
  at: number;
  /** "SOLUSDT"; null for a Replay trade (its log keeps only the result). */
  symbol: string | null;
  side: "LONG" | "SHORT" | null;
  /** Net R once closed, in units of the usual risk; null while still open. */
  r: number | null;
  kind: "real" | "practice" | "replay";
  /** Real trades only: on an engine signal and inside the day's limits. */
  followed: boolean | null;
}

/** Your trades since the plan started: logged ones (not the engine's auto-tracked signals) and Replay. */
export function challengeTrades(tracked: TrackedTrade[], replay: ReplayLogEntry[], startMs: number): ChallengeTrade[] {
  const out: ChallengeTrade[] = [];
  for (const t of tracked) {
    if (t.created_at < startMs || t.direction === "WATCH" || t.status === "cancelled" || t.source === "auto") continue;
    const open = t.status === "active" || t.status === "tp1_hit";
    const real = t.mode === "real";
    // In units of the usual risk: a half-size (Against-BTC) trade counts half.
    const scale = t.risk_scale != null && t.risk_scale > 0 ? t.risk_scale : 1;
    out.push({
      at: t.created_at,
      symbol: t.symbol,
      side: t.direction === "SHORT" ? "SHORT" : "LONG",
      r: open || t.r_multiple == null ? null : t.r_multiple * scale,
      kind: real ? "real" : "practice",
      followed: real ? t.entry_type === "signal" && !t.rule_break : null,
    });
  }
  for (const e of replay) if (e.at >= startMs) out.push({ at: e.at, symbol: null, side: null, r: e.r, kind: "replay", followed: null });
  return out.sort((a, b) => a.at - b.at);
}

/** One plan day's trades and how the day went (a trade belongs to the day it was opened, UTC). */
export interface DayStat {
  /** 00:00 UTC of the day (epoch ms). */
  startMs: number;
  trades: ChallengeTrade[];
  closed: number;
  won: number;
  lost: number;
  /** Still open: no result yet. */
  open: number;
  /** Sum of the closed trades' R. */
  netR: number;
}

/** A result within ±0.05R is a scratch: neither a win nor a loss. */
export const SCRATCH_R = 0.05;

export interface ChallengeStats {
  /** 1 on the start day; above CHALLENGE_DAYS once it is over. */
  day: number;
  /** One per plan day: a trade was logged that day. */
  practised: boolean[];
  /** One per plan day: its trades and result. */
  days: DayStat[];
  /** Days that ended up / down (by net R), and the best and worst day; null with no closed trades. */
  dayRecord: { won: number; lost: number; best: number | null; worst: number | null };
  today: number;
  trades: number;
  closed: number;
  totalR: number;
  avgR: number | null;
  winRate: number | null;
  real: { trades: number; followed: number };
  /** Share of real trades by the rules; null with no real trades. */
  rulesPct: number | null;
  last7: { trades: number; avgR: number | null };
  milestone: { trades: boolean; rules: boolean; avgR: boolean };
}

const utcDay = (ms: number) => Math.floor(ms / DAY_MS);

export function challengeStats(trades: ChallengeTrade[], startMs: number, nowMs: number): ChallengeStats {
  const first = utcDay(startMs);
  const day = utcDay(nowMs) - first + 1;
  const days: DayStat[] = Array.from({ length: CHALLENGE_DAYS }, (_, i) => ({
    startMs: (first + i) * DAY_MS, trades: [], closed: 0, won: 0, lost: 0, open: 0, netR: 0,
  }));
  for (const t of trades) {
    const d = days[utcDay(t.at) - first];
    if (!d) continue;
    d.trades.push(t);
    if (t.r === null) {
      d.open++;
      continue;
    }
    d.closed++;
    d.netR += t.r;
    if (t.r > SCRATCH_R) d.won++;
    else if (t.r < -SCRATCH_R) d.lost++;
  }
  const practised = days.map(d => d.trades.length > 0);
  const results = days.filter(d => d.closed > 0).map(d => d.netR);
  const closed = trades.filter(t => t.r !== null);
  const sum = (xs: ChallengeTrade[]) => xs.reduce((s, t) => s + (t.r ?? 0), 0);
  const real = trades.filter(t => t.kind === "real");
  const followed = real.filter(t => t.followed).length;
  const recent = closed.filter(t => t.at > nowMs - 7 * DAY_MS);
  const avgR = closed.length > 0 ? sum(closed) / closed.length : null;
  const rulesPct = real.length > 0 ? followed / real.length : null;
  return {
    day,
    practised,
    days,
    dayRecord: {
      won: results.filter(r => r > SCRATCH_R).length,
      lost: results.filter(r => r < -SCRATCH_R).length,
      best: results.length > 0 ? Math.max(...results) : null,
      worst: results.length > 0 ? Math.min(...results) : null,
    },
    today: trades.filter(t => utcDay(t.at) === utcDay(nowMs)).length,
    trades: trades.length,
    closed: closed.length,
    totalR: sum(closed),
    avgR,
    winRate: closed.length > 0 ? closed.filter(t => t.r! > SCRATCH_R).length / closed.length : null,
    real: { trades: real.length, followed },
    rulesPct,
    last7: { trades: recent.length, avgR: recent.length > 0 ? sum(recent) / recent.length : null },
    milestone: {
      trades: trades.length >= GOAL_TRADES,
      // No real trades yet breaks no rule.
      rules: rulesPct === null || rulesPct >= RULES_GOAL,
      avgR: avgR !== null && avgR > 0,
    },
  };
}

// ─── When the plan started: one per browser ─────────────────────────────────

const START_KEY = "vp.challenge.start";
const listeners = new Set<() => void>();
let started: number | null = readStart();

function readStart(): number | null {
  try {
    const v = Number(localStorage.getItem(START_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export const challengeStart = {
  get: () => started,
  /** Start now (or start over); null stops the plan. */
  set(ms: number | null): void {
    started = ms;
    try {
      if (ms === null) localStorage.removeItem(START_KEY);
      else localStorage.setItem(START_KEY, String(ms));
    } catch {
      /* storage blocked: kept for this session */
    }
    for (const fn of listeners) fn();
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export function useChallengeStart(): number | null {
  return useSyncExternalStore(challengeStart.subscribe, challengeStart.get);
}
